import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// import './SmartCityMaps.css';

import 'ol/ol.css';
import './SmartCityMaps.css';

import { defaults as defaultControls } from 'ol/control';
import { createEmpty, extend, getCenter, isEmpty } from 'ol/extent';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { defaults as defaultInteractions } from 'ol/interaction';
import { Tile as TileLayer } from 'ol/layer';
import Heatmap from 'ol/layer/Heatmap';
import type OlLayer from 'ol/layer/Layer';
import VectorLayer from 'ol/layer/Vector';
import OlMap from 'ol/Map';
import { fromLonLat, toLonLat } from 'ol/proj';
import Cluster from 'ol/source/Cluster';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Text } from 'ol/style';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import View from 'ol/View';

import Colorize from 'ol-ext/filter/Colorize';
import 'ol-ext/filter/Colorize';

// import { decode } from '@googlemaps/polyline-codec';
import LineString from 'ol/geom/LineString';
import Overlay from 'ol/Overlay';
import IconDarkMap from '@/assets/icon/icon-dark-map.svg';
import IconDefault from '@/assets/icon/icon-default.svg';
import IconNetwork from '@/assets/icon/icon-network.svg';
import IconReturn from '@/assets/icon/icon-return.svg';
import IconRoadMap from '@/assets/icon/icon-road-map.svg';
import IconSatelite from '@/assets/icon/icon-satelite-map.svg';
import Ambulance from '@/assets/smartcity/ambulance-icon.svg';
import AmbulancePoi from '@/assets/smartcity/ambulance-poi.svg';
import AmbulancePoiRaw from '@/assets/smartcity/ambulance-poi.svg?raw';
import CCTVPoi from '@/assets/smartcity/cctv-poi.svg';
import FireStation from '@/assets/smartcity/fireStation-poi.svg';
import GovernmentPoi from '@/assets/smartcity/government-poi.svg';
import HealthcarePoi from '@/assets/smartcity/healthcare-poi.svg';
import Incident from '@/assets/smartcity/incident-icon.svg';
import IncidentCompleted from '@/assets/smartcity/incident-icon-completed.svg';
import LandmarkPoi from '@/assets/smartcity/landmark-poi.svg';
import Mosque from '@/assets/smartcity/mosque-icon.svg';
import POIGreen from '@/assets/smartcity/poi-green.svg';
import POIOrange from '@/assets/smartcity/poi-orange.svg';
import POIRed from '@/assets/smartcity/poi-red.svg';
import PolicePoi from '@/assets/smartcity/police-poi.svg';
import StartPoint from '@/assets/smartcity/startpoint-icon.svg';
import TrafficLightPoi from '@/assets/smartcity/trafficlight-poi.svg';
import TrainPoi from '@/assets/smartcity/train-poi.svg';
import Wifi from '@/assets/smartcity/wifi-icon.svg';
import WifiPoi from '@/assets/smartcity/wifi-poi.svg';
import BoxMaps from '@/assets/smartvill/icon/box-maps.svg';
import CrossRoadMaps from '@/assets/smartvill/icon/crossroad-maps.svg';
import CrossWalkMaps from '@/assets/smartvill/icon/crosswalk-maps.svg';
import IndoorAirMaps from '@/assets/smartvill/icon/indoorAir-maps.svg';
import OutdoorAirMaps from '@/assets/smartvill/icon/outdoorAir-maps.svg';
import RadarMaps from '@/assets/smartvill/icon/radar-maps.svg';
import SpeakerMaps from '@/assets/smartvill/icon/radar-maps.svg';
import VideoMaps from '@/domains/traffic-monitoring/components/VideoMaps/VideoMaps';
import { getHealthcarePoiIcon } from '@/domains/traffic-monitoring/constants/healthcarePoi.constant';
import ModalMapTooltip from '@/shared/components/ui/ModalMapTooltip/ModalMapTooltip';
import type {
  ModalMapTooltipItem,
  ModalMapTooltipProps,
} from '@/shared/components/ui/ModalMapTooltip/ModalMapTooltip.types';

import {
  canToggleProvider,
  createBaseTileSource,
  createSatelliteTileSource,
  getActiveProvider,
  isMapAvailable,
  providerHasSatellite,
  setProviderPreference,
} from '@/shared/config/map';
import { createMapGridLayer, MAP_GRID_BG } from '@/shared/config/mapPlaceholder';
import { useMapProviderReady } from '@/shared/config/useMapProviderReady';
import { toWhatsAppNumber } from '@/shared/utils/phone';

// const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
// console.log('Google API Key:', GOOGLE_API_KEY);

// console.log(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);

import { useThemeStore } from '@/store/themeStore';

export interface CctvClickData {
  name: string;
  address: string;
  latlong: string;
  streamUrl: string | null;
  typeVideo: 'online' | 'offline' | 'maintenance';
}

interface Marker {
  id: string;
  lat: number;
  lng: number;
  type: string;
  // Optional badge drawn over the icon (e.g. event count on a CCTV POI); shown
  // only when > 1. Markers without it render as a plain icon, as before.
  count?: number;
  tooltip?: {
    title: string;
    description: string;
    // Incident markers only: the ambulance dispatched to this incident and
    // the facility that owns it. Already-translated strings (see
    // useSmartCityDashboard.ts) - this component renders them verbatim.
    ambulance?: string;
    owner?: string;
    // healthcarepoi markers only: the facility's PIC (person-in-charge)
    // contact number, shown as a tooltip line under the title. Clicking the
    // marker opens a WhatsApp chat to this number (see the singleclick
    // handler).
    phone?: string;
  };
  modalTooltip?: ModalMapTooltipProps;
  // Present on cctvpoi markers - clicking one calls onCctvClick instead of
  // opening the generic modalTooltip popup.
  cctv?: CctvClickData;
  // Present on incident markers - clicking one opens the report detail page
  // in a new tab instead of the generic modalTooltip popup.
  reportId?: number;
  // 'ambulancepoi' markers only: tints the POI pin background (see
  // getAmbulancePoiIcon) to match the ambulance's status color in the fleet
  // panel.
  color?: string;
  // 'ambulancepoi' markers only: tints the ambulance glyph on the pin -
  // white by default, some statuses use a colored glyph instead.
  iconColor?: string;
  // Shown as a stacked-marker popup row's second line (see the singleclick
  // handler's cluster branch) - e.g. an ambulance's home facility name.
  // Ignored outside that popup.
  subtitle?: string;
  // 'healthcarepoi' markers only, read only when the map's healthcareTypeIcons
  // prop is on: the facility type code (HLTH_FAC_TP_*) that picks the badge -
  // Hospital / Public Health Center / Clinic / Other (see
  // healthcarePoi.constant.ts). Missing or unknown ⇒ the "Other" badge.
  facilityType?: string;
}

interface Polyline {
  id: string;
  color?: string;
  width?: number;
  points: number[][];
}

export interface HeatmapPoint {
  id: string;
  lat: number;
  lng: number;
  // 0-1 congestion intensity at this point.
  weight: number;
}

export interface LegendItem {
  icon: string;
  label: string;
}

interface Props {
  coords: {
    lat: number;
    lng: number;
  };
  showModal?: boolean;
  zoom?: number;
  markers: Marker[];
  polylines?: Polyline[];
  // Congestion heatmap layer, drawn under the POI markers. Omitted/empty ⇒ no
  // heatmap layer is added.
  heatmapPoints?: HeatmapPoint[];
  // Set from the site list (2nd depth click) to fly the map to a single POI at
  // the deepest zoom stage. `nonce` changes per click so repeated clicks on the
  // same POI still re-trigger the animation. `id` (a marker id) is optional -
  // when present, that marker blinks once the fly-to animation lands.
  // `ids` (2+ marker ids) switches to fit-to-bounds instead of fly-to-point,
  // framing every listed marker in view (e.g. PSCDashboard.tsx's incident +
  // its related on-going ambulances) - every marker in `ids` then blinks once
  // the fit lands, same as the single-marker `id` case. `lat`/`lng` are only
  // used for the single-marker fly-to and can be omitted when `ids` is set.
  focusTarget?: {
    lat?: number;
    lng?: number;
    nonce: number;
    id?: string;
    ids?: string[];
  } | null;
  // Fired when the small VideoMaps overlay's expand button is clicked, so the
  // caller can open a bigger view (e.g. VideoModalMaps). The small overlay
  // itself is fully owned and rendered by this component.
  onCctvExpand?: (cctv: CctvClickData) => void;
  // When true, cctvpoi markers show no VideoMaps card until that marker is
  // clicked. Every clicked camera's card stays open (clicking another marker -
  // cctv or not - never closes one); clicking the empty map closes them all.
  // When false (default), every visible cctvpoi marker keeps its card open, as
  // the PSC dashboard's "Show CCTV" flow needs.
  openCctvCardsOnClick?: boolean;
  // When true, every marker's hover tooltip and cluster popup drop their
  // lat/lng line entirely.
  hideCoordinates?: boolean;
  // Static key explaining what the marker icons mean (e.g. "red pin = recent
  // event"), drawn as a pill overlay top-right. Omitted ⇒ no legend shown.
  legend?: LegendItem[];
  // When true, 'healthcarepoi' markers are drawn as round per-type badges
  // (picked by each marker's facilityType) instead of the single healthcare
  // pin. Only the PSC dashboard turns this on; every other map keeps the pin.
  healthcareTypeIcons?: boolean;
}

// Cluster/focus zoom stages: two fixed intermediate stages, then a final
// zoom-to-fit. MAX_FIT_ZOOM is the deepest (highest) of the 3 stages — the
// level the site list focuses a POI to. Module-scoped so both the cluster-click
// effect and the focus effect share them.
const INTERMEDIATE_STAGES = [12, 15];
const MAX_FIT_ZOOM = 19;

// focusTarget blink: how many on/off flashes, and the delay between each.
const BLINK_CYCLES = 4;
const BLINK_INTERVAL_MS = 180;
const FIT_PADDING = [80, 80, 80, 80];

type FilterableTileLayer = TileLayer & {
  addFilter: (filter: unknown) => void;
};

const iconMapping: Record<string, string> = {
  // SpeakerMaps: SpeakerMaps,
  // IndoorAirMaps: IndoorAirMaps,
  // OutdoorAirMaps: OutdoorAirMaps,
  // CrossRoadMaps: CrossRoadMaps,
  // CrossWalkMaps: CrossWalkMaps,
  // BoxMaps: BoxMaps,
  // RadarMaps: RadarMaps,

  Ambulance: Ambulance,
  StartPoint: StartPoint,
  Incident: Incident,
  // A completed/rejected report's incident pin - same badge shape as Incident,
  // greyed out so it reads as history rather than something still needing
  // attention. See PSCDashboard.tsx, which retypes past-report markers to this.
  IncidentCompleted: IncidentCompleted,
  Mosque: Mosque,
  Wifi: Wifi,

  green: POIGreen,
  orange: POIOrange,
  red: POIRed,

  cctvpoi: CCTVPoi,
  policepoi: PolicePoi,
  trafficlightpoi: TrafficLightPoi,
  healthcarepoi: HealthcarePoi,
  firestation: FireStation,
  ambulancepoi: AmbulancePoi,
  governmentpoi: GovernmentPoi,
  trainpoi: TrainPoi,
  wifipoi: WifiPoi,
  landmarkpoi: LandmarkPoi,
};

// ambulance-poi.svg's pin background and ambulance glyph are drawn with these
// fills - swapped out per-marker for the ambulance's status colors (see
// usePscDashboard.ts's ambulanceStatusColors) so the map pin matches the row
// it has in the fleet panel on the right. `.replace` (no /g flag) only
// touches the FIRST match of each fill, which is exactly the pin path and the
// glyph path respectively - the SVG's only other "white" is the unrelated
// <clipPath> rect further down the document.
const AMBULANCE_POI_DEFAULT_BACKGROUND = '#007AFF';
const AMBULANCE_POI_DEFAULT_ICON = 'white';
const ambulancePoiIconCache = new Map<string, string>();
const getAmbulancePoiIcon = (background?: string, iconColor?: string): string => {
  if (!background && !iconColor) return AmbulancePoi;
  const cacheKey = `${background ?? AMBULANCE_POI_DEFAULT_BACKGROUND}|${iconColor ?? AMBULANCE_POI_DEFAULT_ICON}`;
  const cached = ambulancePoiIconCache.get(cacheKey);
  if (cached) return cached;
  let tinted = AmbulancePoiRaw;
  if (background) {
    tinted = tinted.replace(`fill="${AMBULANCE_POI_DEFAULT_BACKGROUND}"`, `fill="${background}"`);
  }
  if (iconColor) {
    tinted = tinted.replace(`fill="${AMBULANCE_POI_DEFAULT_ICON}"`, `fill="${iconColor}"`);
  }
  const dataUri = `data:image/svg+xml;utf8,${encodeURIComponent(tinted)}`;
  ambulancePoiIconCache.set(cacheKey, dataUri);
  return dataUri;
};

// OpenLayers wraps the element handed to an Overlay in its own positioned
// container ('ol-overlay-container') and drives that wrapper's position with an
// inline transform, so each card is a stacking context of its own: a z-index set
// on the card only orders that card's own contents and can never lift it above a
// sibling card. The wrapper is what competes for paint order, so a raise has to
// be applied there. Kept below the map controls, which SmartCityMaps.css holds
// at 20.
const CCTV_RAISED_Z_INDEX = '5';

const getOverlayWrapper = (overlay: Overlay): HTMLElement | null => {
  const wrapper = overlay.getElement()?.parentElement ?? null;
  return wrapper?.classList.contains('ol-overlay-container') ? wrapper : null;
};

const SmartCityMapsInner = ({
  coords,
  zoom = 11,
  markers,
  focusTarget,
  polylines,
  heatmapPoints,
  showModal = false,
  onCctvExpand,
  openCctvCardsOnClick = false,
  hideCoordinates = false,
  legend,
  healthcareTypeIcons = false,
}: Props) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<OlMap | null>(null);
  const clusterLayerRef = useRef<VectorLayer | null>(null);
  const heatmapLayerRef = useRef<Heatmap | null>(null);

  const hoveredFeatureRef = useRef<any>(null);
  // The focusTarget blink effect below toggles this on/off - read by the
  // cluster style function so a blinking marker still lights up even when
  // it's grouped into a cluster bubble (whose style otherwise fully replaces
  // the individual feature's own style, hiding the glow entirely). A Set
  // (not a single id) so a multi-marker focusTarget (e.g. an incident + its
  // related on-going ambulances) can blink every one of them at once.
  const blinkFeatureIdsRef = useRef<Set<string>>(new Set());
  const blinkOnRef = useRef(false);

  const polylineLayerRef = useRef<VectorLayer | null>(null);
  const tooltipOverlayRef = useRef<Overlay | null>(null);
  const tooltipElementRef = useRef<HTMLDivElement | null>(null);
  const modalTooltipOverlayRef = useRef<Overlay | null>(null);
  const modalTooltipElementRef = useRef<HTMLDivElement | null>(null);
  const [selectedModalTooltip, setSelectedModalTooltip] = useState<ModalMapTooltipProps | null>(
    null
  );
  // One always-on OL Overlay + portal container per visible cctv marker (not
  // a single shared overlay) - when the "Show CCTV" toggle is on, every
  // camera's VideoMaps card is open at once, not just the last-clicked one.
  const cctvOverlaysRef = useRef<Map<string, { overlay: Overlay; container: HTMLDivElement }>>(
    new Map()
  );
  const [cctvOverlayIds, setCctvOverlayIds] = useState<string[]>([]);
  // The cctvpoi marker whose card sits on top of the others. Set on every
  // camera click in both modes, so the latest card opened never ends up behind
  // a card that was opened earlier.
  const [topCctvId, setTopCctvId] = useState<string | null>(null);
  // openCctvCardsOnClick mode: the cctvpoi markers whose cards are currently
  // open. Cards accumulate as pins are clicked and only the empty map (or the
  // marker disappearing) clears them - clicking another marker never closes
  // an open one.
  const [openCctvIds, setOpenCctvIds] = useState<Set<string>>(new Set());

  const layerMapRef = useRef<Record<string, TileLayer> | null>(null);
  const isDark = useThemeStore((state) => state.isDark);
  const isDarkRef = useRef(isDark);

  // Excludes the heatmap layer from click/hover hit-testing so it stays a
  // purely visual overlay - it has no tooltip/modal data of its own, so
  // letting a click land on it would open an empty modal tooltip.
  const interactiveLayerFilter = (layer: OlLayer) => layer !== heatmapLayerRef.current;

  // Shared by the focusTarget effect (below) and the singleclick handler's
  // cluster-popup item click (inside the init effect) - both want the exact
  // same "find this marker's underlying feature" / "glow it a few times"
  // behavior. Only close over stable refs, so it's safe for the init effect's
  // singleclick handler to capture a render-time copy once at mount, same as
  // showModal/openCctvCardsOnClick already do there (see the exhaustive-deps
  // exemption comment on that effect). Wrapped in useCallback so the other
  // effects that DO track deps (focusTarget) can list them safely too.
  const findFeature = useCallback((id: string) => {
    const clusterSource = clusterLayerRef.current?.getSource() as Cluster | undefined;
    return clusterSource
      ?.getSource()
      ?.getFeatures()
      .find((f) => f.get('id') === id);
  }, []);

  // Lights up every feature in `features` at once (a soft glow ring drawn
  // behind each icon, first in its style array so the icon still renders on
  // top of it) - a single marker for a plain fly-to, or several for a
  // fit-to-bounds case, all blinking in sync.
  const blinkFeatures = useCallback((features: Feature[]) => {
    if (features.length === 0) return;

    const entries = features.map((feature) => {
      // This component only ever calls feature.setStyle() with a Style or
      // Style[] (never a StyleFunction), so the cast just narrows back to that.
      const originalStyle = feature.getStyle() as Style | Style[] | undefined;
      const originalStyles = Array.isArray(originalStyle)
        ? originalStyle
        : originalStyle
          ? [originalStyle]
          : [];
      const glowStyle = new Style({
        image: new CircleStyle({
          radius: 24,
          fill: new Fill({ color: 'rgba(255, 205, 0, 0.45)' }),
          stroke: new Stroke({ color: 'rgba(255, 205, 0, 0.9)', width: 2 }),
        }),
      });
      return { feature, originalStyle, litStyles: [glowStyle, ...originalStyles] };
    });

    blinkFeatureIdsRef.current = new Set(entries.map(({ feature }) => feature.get('id')));
    const totalSteps = BLINK_CYCLES * 2;
    let step = 0;

    const toggle = () => {
      const lit = step % 2 === 0;
      entries.forEach(({ feature, originalStyle, litStyles }) => {
        feature.setStyle(lit ? litStyles : originalStyle);
      });
      // Mirrors the features' on/off state so the cluster style function
      // (which never reads a clustered member's own style) can draw the
      // same glow behind the cluster bubble when needed.
      blinkOnRef.current = lit;
      clusterLayerRef.current?.changed();
      step += 1;
      if (step < totalSteps) {
        setTimeout(toggle, BLINK_INTERVAL_MS);
      } else {
        blinkFeatureIdsRef.current = new Set();
      }
    };
    toggle();
  }, []);

  // Same fly-to-point + blink animation PSCDashboard.tsx's card click drives
  // externally via the focusTarget prop (see that effect below), triggered
  // instead from inside the map itself - a cluster popup's item click (e.g.
  // an ambulance/incident row) has no report/card list to round-trip through.
  const flyToMarker = useCallback(
    (id: string, lat: number, lng: number) => {
      const map = mapRef.current;
      if (!map) return;
      map
        .getView()
        .animate(
          { center: fromLonLat([lng, lat]), zoom: MAX_FIT_ZOOM, duration: 500 },
          (completed) => {
            if (!completed) return;
            const feature = findFeature(id);
            if (feature) blinkFeatures([feature]);
          }
        );
    },
    [findFeature, blinkFeatures]
  );

  useEffect(() => {
    isDarkRef.current = isDark;
  }, [isDark]);

  useEffect(() => {
    if (!layerMapRef.current) return;

    Object.entries(layerMapRef.current).forEach(([key, layer]) => {
      layer.setVisible(isDark ? key === 'canvasdark' : key === 'canvaslight');
    });
  }, [isDark]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: showModal is read inside a pointermove handler bound once at map init; adding it here would re-initialize the whole map on toggle
  useEffect(() => {
    if (!mapElementRef.current) return;

    // const satelliteSource = createSatelliteTileSource();

    const layerMap: Record<string, TileLayer> = {
      base: new TileLayer({
        visible: false,
        source: createBaseTileSource(),
      }),

      canvaslight: new TileLayer({
        visible: !isDarkRef.current,
        source: createBaseTileSource(),
      }),

      canvasdark: new TileLayer({
        visible: isDarkRef.current,
        source: createBaseTileSource(),
      }),

      // Only the online provider (vworld) has satellite imagery.
      //   ...(satelliteSource
      //     ? {
      //         satellite: new TileLayer({
      //           visible: false,
      //           source: satelliteSource,
      //         }),
      //       }
      //     : {}),
    };
    layerMapRef.current = layerMap;

    const darkLayer = layerMap.canvasdark as FilterableTileLayer;

    const enhance = new Colorize();
    enhance.setFilter({
      operation: 'enhance',
      value: 0.1,
    });

    const darkColor = new Colorize();
    darkColor.setFilter({
      operation: 'color',
      red: 40,
      green: 40,
      blue: 40,
      value: 1,
    });

    const invert = new Colorize();
    invert.setFilter('invert');

    darkLayer.addFilter(enhance);
    darkLayer.addFilter(darkColor);
    darkLayer.addFilter(invert);

    // Cluster click zoom uses the module-scoped INTERMEDIATE_STAGES / MAX_FIT_ZOOM
    // / FIT_PADDING. maxZoom blocks going past the fit cap by any means;
    // double-click zoom is disabled so clicks only drive these stages.
    const map = new OlMap({
      target: mapElementRef.current,
      // Grid backdrop appended after the base layers (so the dark-theme invert
      // filter does not flip it) and before the cluster layer added below.
      layers: [...Object.values(layerMap), createMapGridLayer()],
      // Zoom control stays (custom icon buttons below are appended into it) -
      // only the "© ..." attribution control is dropped.
      controls: defaultControls({ attribution: false }),
      interactions: defaultInteractions({ doubleClickZoom: false }),
      view: new View({
        center: fromLonLat([coords.lng, coords.lat]),
        zoom,
        maxZoom: MAX_FIT_ZOOM,
      }),
    });

    // hover

    // map.on('pointermove', (event) => {
    //   const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature);

    //   const target = map.getTargetElement();
    //   target.style.cursor = feature ? 'pointer' : '';

    //   hoveredFeatureRef.current = feature;

    //   if (clusterLayerRef.current) {
    //     clusterLayerRef.current.changed();
    //   }
    // });

    // Cursor feedback is bound unconditionally: every POI is clickable in both
    // modes — showModal only changes *what* the click opens (modal tooltip vs
    // hover tooltip) — so gating this on !showModal left the traffic dashboard's
    // markers looking inert. Skipped mid-drag, where hit-testing every move is
    // wasted work and the grab cursor should win anyway.
    map.on('pointermove', (event) => {
      if (event.dragging) return;
      const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature, {
        layerFilter: interactiveLayerFilter,
      });
      map.getTargetElement().style.cursor = feature ? 'pointer' : '';
    });

    if (!showModal) {
      map.on('pointermove', (event) => {
        const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature, {
          layerFilter: interactiveLayerFilter,
        });

        if (feature && tooltipOverlayRef.current && tooltipElementRef.current) {
          // const tooltip = feature.get('tooltip');
          const clusteredFeatures = feature.get('features');

          if (clusteredFeatures && clusteredFeatures.length === 1) {
            const originalFeature = clusteredFeatures[0];

            const tooltip = originalFeature.get('tooltip');

            if (tooltip) {
              const lat = originalFeature.get('lat');
              const lng = originalFeature.get('lng');
              const coordinates =
                !hideCoordinates && typeof lat === 'number' && typeof lng === 'number'
                  ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
                  : '';

              // Incident markers only - see the Marker.tooltip type.
              const ambulanceLine =
                tooltip.ambulance != null
                  ? `<div style="font-size: 14px; margin-top: 4px;">Ambulance: ${tooltip.ambulance}</div>`
                  : '';
              const ownerLine =
                tooltip.owner != null
                  ? `<div style="font-size: 14px; margin-top: 2px;">Owner: ${tooltip.owner}</div>`
                  : '';

              // Ambulance markers only (see Marker.subtitle): lead with the
              // vehicle's home facility name instead of the plate number, so
              // hovering the map answers "whose ambulance is this" first -
              // the plate number moves down to where coordinates normally sit,
              // and coordinates get their own line below that instead of
              // being dropped.
              const subtitle = originalFeature.get('subtitle') as string | undefined;
              const headline = subtitle ?? tooltip.title;
              const secondLine = subtitle ? tooltip.title : coordinates;
              // No second line (hidden coordinates, no subtitle) - emit no row
              // rather than an empty one, so the next line sits under the title.
              const secondLineBlock = secondLine
                ? `<div style="font-size: 14px; margin-top: 4px;">${secondLine}</div>`
                : '';
              const phoneGap = secondLine ? '2px' : '4px';
              // healthcarepoi markers only - see the Marker.tooltip type.
              const phoneLine =
                tooltip.phone != null && tooltip.phone !== ''
                  ? `<div style="font-size: 14px; margin-top: ${phoneGap};">PIC Phone: ${tooltip.phone}</div>`
                  : '';
              const coordinatesLine =
                subtitle && coordinates
                  ? `<div style="font-size: 14px; margin-top: 2px;">${coordinates}</div>`
                  : '';

              tooltipElementRef.current.innerHTML = `
            <div style=" border-radius: 8px;">
              <div style="font-weight: 600; font-size: 18px;">
                ${headline}
              </div>

              ${secondLineBlock}
              ${phoneLine}
              ${coordinatesLine}
              ${ambulanceLine}
              ${ownerLine}
            </div>
          `;

              tooltipElementRef.current.style.display = 'block';

              tooltipOverlayRef.current.setPosition(
                (originalFeature.getGeometry() as Point).getCoordinates()
              );
            } else {
              tooltipElementRef.current.style.display = 'none';
            }
          }

          // tooltipElementRef.current.style.display = 'block';

          tooltipOverlayRef.current.setPosition((feature.getGeometry() as Point).getCoordinates());
        } else {
          if (tooltipElementRef.current) {
            tooltipElementRef.current.style.display = 'none';
          }
        }

        hoveredFeatureRef.current = feature;

        if (clusterLayerRef.current) {
          clusterLayerRef.current.changed();
        }
      });
    }

    mapRef.current = map;

    if (tooltipElementRef.current) {
      const overlay = new Overlay({
        element: tooltipElementRef.current,
        offset: [0, -10],
        positioning: 'bottom-center',
        stopEvent: false,
      });

      map.addOverlay(overlay);

      tooltipOverlayRef.current = overlay;
    }

    if (modalTooltipElementRef.current) {
      const modalOverlay = new Overlay({
        element: modalTooltipElementRef.current,
        offset: [0, -14],
        positioning: 'bottom-center',
        stopEvent: true,
      });

      map.addOverlay(modalOverlay);

      modalTooltipOverlayRef.current = modalOverlay;
    }
    // 3-stage cluster zoom: fixed intermediate stages, then a final zoom-to-fit
    // so every POI in the cluster is visible. Centered on the clicked cluster.
    map.on('singleclick', (event) => {
      const clicked = map.forEachFeatureAtPixel(event.pixel, (f) => f, {
        layerFilter: interactiveLayerFilter,
      });
      if (!clicked) {
        setSelectedModalTooltip(null);
        modalTooltipElementRef.current?.style.setProperty('display', 'none');
        // Clicking empty map closes every open cctv card.
        if (openCctvCardsOnClick) setOpenCctvIds(new Set());
        return;
      }

      const members = (clicked.get('features') as Feature[] | undefined) ?? [clicked as Feature];

      if (members.length === 1) {
        const originalFeature = members[0];
        if (!originalFeature) {
          return;
        }

        const cctv = originalFeature.get('cctv') as CctvClickData | undefined;
        if (cctv) {
          const id = originalFeature.get('id') as string;
          if (openCctvCardsOnClick) {
            // Opens this camera's card without touching any other open card -
            // only the empty map closes them all.
            setOpenCctvIds((prev) => {
              if (prev.has(id)) return prev;
              const next = new Set(prev);
              next.add(id);
              return next;
            });
          }
          // Raised in both modes: the card just clicked goes above every other
          // open card, whichever was on top before.
          setTopCctvId(id);
          return;
        }

        const reportId = originalFeature.get('reportId') as number | undefined;
        if (reportId != null) {
          // An explicit width/height (rather than a bare '_blank') is what makes
          // browsers open a separate window instead of a new tab.
          window.open(
            `/dashboard/details-dashboard?reportId=${reportId}`,
            '_blank',
            'width=1200,height=800,resizable=yes,scrollbars=yes,noopener'
          );
          return;
        }

        // healthcarepoi markers only: open a WhatsApp chat with the
        // facility's PIC instead of the generic modalTooltip popup.
        const clickedTooltip = originalFeature.get('tooltip') as { phone?: string } | undefined;
        if (clickedTooltip?.phone) {
          const waNumber = toWhatsAppNumber(clickedTooltip.phone);
          if (waNumber) {
            window.open(`https://wa.me/${waNumber}`, '_blank', 'noopener,noreferrer');
          }
          return;
        }

        const geometry = originalFeature.getGeometry();
        const modalTooltip = originalFeature.get('modalTooltip') as
          | ModalMapTooltipProps
          | undefined;

        // Only for a marker that actually carries one (e.g. Traffic Dashboard's
        // per-CCTV event list) - otherwise leave whatever was already open (or
        // closed) alone rather than popping an empty "-" placeholder card.
        if (
          modalTooltip &&
          geometry &&
          modalTooltipOverlayRef.current &&
          modalTooltipElementRef.current
        ) {
          setSelectedModalTooltip(modalTooltip);
          modalTooltipElementRef.current.style.display = 'block';
          modalTooltipOverlayRef.current.setPosition((geometry as Point).getCoordinates());
        }

        return;
      }

      // A real cluster of 2+ distinct markers (the white-circle/stacked-icons
      // bubble - see the style function above) - show the same "N Items" list
      // popup as a marker's own modalTooltip, built on the fly from each
      // clustered member, instead of only ever zooming in. Falls back to the
      // zoom cascade below when no member yields anything listable.
      if (modalTooltipOverlayRef.current && modalTooltipElementRef.current) {
        const items: ModalMapTooltipItem[] = [];
        const clickActions = new Map<string, () => void>();

        members.forEach((member) => {
          const memberModal = member.get('modalTooltip') as ModalMapTooltipProps | undefined;
          if (memberModal?.item?.length) {
            memberModal.item.forEach((memberItem) => {
              items.push(memberItem);
              if (memberItem.id != null && memberModal.onItemClick) {
                clickActions.set(memberItem.id, () => memberModal.onItemClick?.(memberItem));
              }
            });
            return;
          }

          const cctv = member.get('cctv') as CctvClickData | undefined;
          if (cctv) {
            const memberId = member.get('id') as string;
            const itemId = `cctv-${memberId}`;
            items.push({ title: cctv.name, id: itemId });
            clickActions.set(itemId, () => {
              if (openCctvCardsOnClick) {
                setOpenCctvIds((prev) => {
                  if (prev.has(memberId)) return prev;
                  const next = new Set(prev);
                  next.add(memberId);
                  return next;
                });
              }
              setTopCctvId(memberId);
            });
            return;
          }

          const reportId = member.get('reportId') as number | undefined;
          const tooltip = member.get('tooltip') as
            | { title: string; description: string }
            | undefined;
          const subtitle = member.get('subtitle') as string | undefined;
          const memberId = member.get('id') as string | undefined;
          const itemId = reportId != null ? `report-${reportId}` : memberId;
          items.push({ title: tooltip?.title, subtitle: subtitle ?? null, id: itemId ?? null });
          // Same map-only action PSCDashboard.tsx's own ambulance/report list
          // triggers on a card click (onCardClick/focusOnMarker) - zoom to
          // and glow this one marker, rather than navigating away from the
          // map (that's still one tap away via the marker's own click).
          const memberLat = member.get('lat') as number | undefined;
          const memberLng = member.get('lng') as number | undefined;
          if (itemId && memberId && memberLat != null && memberLng != null) {
            clickActions.set(itemId, () => flyToMarker(memberId, memberLat, memberLng));
          }
        });

        if (items.length > 0) {
          const clusterGeometry = clicked.getGeometry();
          const clusterCoords =
            !hideCoordinates && clusterGeometry
              ? toLonLat((clusterGeometry as Point).getCoordinates())
              : null;
          setSelectedModalTooltip({
            coords: clusterCoords ? { lng: clusterCoords[0], lat: clusterCoords[1] } : null,
            item: items,
            onItemClick: (clickedItem) => {
              if (clickedItem.id) clickActions.get(clickedItem.id)?.();
            },
          });
          modalTooltipElementRef.current.style.display = 'block';
          if (clusterGeometry) {
            modalTooltipOverlayRef.current.setPosition((clusterGeometry as Point).getCoordinates());
          }
          return;
        }
      }

      setSelectedModalTooltip(null);
      modalTooltipElementRef.current?.style.setProperty('display', 'none');

      const extent = createEmpty();
      members.forEach((member) => {
        const geometry = member.getGeometry();
        if (geometry) extend(extent, geometry.getExtent());
      });
      if (isEmpty(extent)) return;

      const view = map.getView();
      const currentZoom = view.getZoom() ?? zoom;
      const nextStage = INTERMEDIATE_STAGES.find((stageZoom) => stageZoom > currentZoom + 0.01);

      if (nextStage !== undefined) {
        // Intermediate depth: jump to the next fixed stage.
        view.animate({ center: getCenter(extent), zoom: nextStage, duration: 500 });
      } else {
        // Final depth: fit the view so every POI in the cluster is visible.
        view.fit(extent, { padding: FIT_PADDING, maxZoom: MAX_FIT_ZOOM, duration: 500 });
      }
    });

    const changeLayer = (selected: keyof typeof layerMap) => {
      Object.entries(layerMap).forEach(([key, layer]) => {
        layer.setVisible(key === selected);
      });
    };

    const resetView = () => {
      map.getView().animate({
        center: fromLonLat([coords.lng, coords.lat]),
        zoom,
        duration: 800,
      });
    };

    setTimeout(() => {
      const zoomControl = document.querySelector('.ol-zoom');
      if (!zoomControl) return;

      zoomControl.querySelectorAll('.custom-icon-button').forEach((btn) => {
        btn.remove();
      });

      const active = getActiveProvider();
      const providerTitle =
        active === 'vworld'
          ? '온라인 지도(VWorld) 사용 중, 클릭 시 오프라인 지도'
          : '오프라인 지도 사용 중, 클릭 시 온라인 지도';

      type MapButton = { src: string; action: () => void; title?: string; opacity?: number };
      const buttons: MapButton[] = [
        { src: IconReturn, action: resetView },
        // { src: IconDefault, action: () => changeLayer('base') },
        { src: IconRoadMap, action: () => changeLayer('canvaslight') },
        { src: IconDarkMap, action: () => changeLayer('canvasdark') },
        // ...(providerHasSatellite()
        //   ? [{ src: IconSatelite, action: () => changeLayer('satellite') }]
        //   : []),
        // ...(canToggleProvider()
        //   ? [
        //       {
        //         src: IconNetwork,
        //         title: providerTitle,
        //         opacity: active === 'vworld' ? 1 : 0.4,
        //         action: () => setProviderPreference(active === 'vworld' ? 'local' : 'vworld'),
        //       },
        //     ]
        //   : []),
      ];

      buttons.forEach((btnData) => {
        const button = document.createElement('button');
        button.className = 'custom-icon-button';
        const opacityStyle = btnData.opacity != null ? ` style="opacity:${btnData.opacity}"` : '';
        button.innerHTML = `<img src="${btnData.src}" width="18" height="18"${opacityStyle} />`;
        if (btnData.title) button.title = btnData.title;
        button.onclick = btnData.action;
        zoomControl.appendChild(button);
      });
    }, 100);

    return () => {
      map.setTarget(undefined);
    };
  }, [coords.lat, coords.lng, zoom]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (clusterLayerRef.current) {
      mapRef.current.removeLayer(clusterLayerRef.current);
    }

    const features = markers.map((item) => {
      // const feature = new Feature({
      //   geometry: new Point(fromLonLat([item.lng, item.lat])),
      // });

      const feature = new Feature({
        geometry: new Point(fromLonLat([item.lng, item.lat])),
      });

      feature.set('id', item.id);
      // Read back by the hover tooltip, which branches per POI kind.
      feature.set('type', item.type);
      feature.set('tooltip', item.tooltip);
      feature.set('modalTooltip', item.modalTooltip);
      feature.set('cctv', item.cctv);
      feature.set('reportId', item.reportId);
      feature.set('count', item.count);
      feature.set('lat', item.lat);
      feature.set('lng', item.lng);
      feature.set('subtitle', item.subtitle);

      const isHealthcareBadge = healthcareTypeIcons && item.type === 'healthcarepoi';
      const iconSrc =
        item.type === 'ambulancepoi'
          ? getAmbulancePoiIcon(item.color, item.iconColor)
          : isHealthcareBadge
            ? getHealthcarePoiIcon(item.facilityType)
            : (iconMapping[item.type] ?? SpeakerMaps);
      // Read back by the cluster preview below instead of introspecting the
      // feature's live style - the focusTarget blink effect temporarily swaps
      // a feature's style to a CircleStyle glow overlay, which has no icon
      // src of its own to read.
      feature.set('iconSrc', iconSrc);

      // Every other POI icon is a pin shape whose point sits at the bottom of
      // its image, so anchoring at [0.5, 1] lines that point up with the
      // marker's coordinate. incident-icon.svg and the healthcare type badges
      // are plain circles with no point - anchoring them at their center instead
      // is what actually centers them on their coordinate (and on-screen once
      // the map flies to them).
      const iconAnchor: [number, number] =
        item.type === 'Incident' || item.type === 'IncidentCompleted' || isHealthcareBadge
          ? [0.5, 0.5]
          : [0.5, 1];

      const iconStyle = new Style({
        image: new Icon({
          src: iconSrc,
          // The 32px healthcare badge at full size matches the on-screen width
          // of the 40px pins drawn at 0.8.
          scale: isHealthcareBadge ? 1 : 0.8,
          anchor: iconAnchor,
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
        }),
      });

      // A count > 1 overlays the number on the marker (e.g. multiple events at
      // one CCTV). Absent/1 keeps the plain icon, so existing callers are
      // unaffected. Text sits slightly above center, over the pin's head.
      if (item.count && item.count > 1) {
        feature.setStyle([
          iconStyle,
          new Style({
            text: new Text({
              text: String(item.count),
              font: 'bold 14px Arial',
              offsetY: -26,
              fill: new Fill({ color: '#FFFFFF' }),
            }),
            zIndex: 1,
          }),
        ]);
      } else {
        feature.setStyle(iconStyle);
      }

      return feature;
    });

    const clusterSource = new Cluster({
      distance: 40,
      source: new VectorSource({
        features,
      }),
    });

    // const clusterLayer = new VectorLayer({
    // 	source: clusterSource,
    // 	style: (feature) => {
    // 		const clustered = feature.get("features");
    // 		const size = clustered.length;

    // 		if (size > 1) {
    // 			return new Style({
    // 				image: new CircleStyle({
    // 					radius: 40,
    // 					fill: new Fill({
    // 						color: "rgba(0,123,229,0.7)",
    // 					}),
    // 				}),
    // 				text: new Text({
    // 					text: String(size),
    // 					font: "bold 18px Arial",
    // 					fill: new Fill({
    // 						color: "#fff",
    // 					}),
    // 				}),
    // 			});
    // 		}

    // 		return clustered[0].getStyle();
    // 	},
    // });

    const clusterLayer = new VectorLayer({
      source: clusterSource,
      style: (feature) => {
        const clustered = feature.get('features');
        const size = clustered.length;
        // Badge text sums each marker's own event count (e.g. traffic events
        // per CCTV) rather than the number of clustered markers, so a cluster
        // of 2 CCTVs with 5 events each reads "10", not "2". Markers without
        // a count (most callers) fall back to 1, so this matches the old
        // marker-count behavior everywhere clustering doesn't carry counts.
        const eventCount = clustered.reduce(
          (sum: number, item: Feature) => sum + (item.get('count') ?? 1),
          0
        );
        const isHovered = hoveredFeatureRef.current === feature;

        if (size > 1) {
          const styles: Style[] = [];

          // A cluster bubble fully replaces its members' own styles below, so
          // a blinking member (see the focusTarget effect) would otherwise
          // never show its glow while grouped - draw it behind the bubble
          // instead whenever the blink target is one of this cluster's members.
          if (
            blinkOnRef.current &&
            clustered.some((item: Feature) => blinkFeatureIdsRef.current.has(item.get('id')))
          ) {
            styles.push(
              new Style({
                image: new CircleStyle({
                  radius: 58,
                  fill: new Fill({ color: 'rgba(255, 205, 0, 0.45)' }),
                  stroke: new Stroke({ color: 'rgba(255, 205, 0, 0.9)', width: 2 }),
                }),
                zIndex: -1,
              })
            );
          }

          styles.push(
            new Style({
              image: new CircleStyle({
                radius: 50,
                fill: new Fill({
                  color: 'rgba(255, 255, 255, 0.80)',
                }),
                stroke: new Stroke({
                  color: 'rgba(0,123,229,0.8)',
                  width: 0.5,
                }),
              }),
              zIndex: 0,
            })
          );

          if (!isHovered) {
            clustered.slice(0, 3).forEach((item: Feature, index: number) => {
              // Read the icon src stored at feature-creation time, not the
              // feature's live style - a focused/blinking marker's style is
              // temporarily a CircleStyle glow overlay with no icon to read.
              const iconSrc = item.get('iconSrc') as string | undefined;
              const maxDisplay = 3;
              const offset2 = size > maxDisplay ? 15 : 10;
              const offsetX = 9;

              styles.push(
                new Style({
                  image: new Icon({
                    src: iconSrc,
                    scale: 0.6,
                    displacement: [-index * offsetX - offset2, 0],
                  }),
                  zIndex: index + 1,
                })
              );
            });
          }

          styles.push(
            new Style({
              text: new Text({
                text: `${eventCount}`,
                font: 'bold 18px Arial',
                offsetX: isHovered ? 0 : 25,
                fill: new Fill({
                  color: 'rgba(0,123,229,1)',
                }),
              }),
              zIndex: 10,
            })
          );

          return styles;
        }

        return clustered[0].getStyle();
      },
    });

    clusterLayerRef.current = clusterLayer;
    mapRef.current.addLayer(clusterLayer);
    clusterLayer.setZIndex(1000);
  }, [markers, healthcareTypeIcons]);

  // Congestion heatmap: one weighted point per CCTV, redrawn whenever the
  // congestion data refreshes. Sits above the base tiles but below the POI
  // markers (zIndex 1000) so pins stay clickable on top of it.
  //
  // The layer itself is created once (lazily, on the first non-empty update)
  // and then only has its source's features swapped in place - it is never
  // removed/re-added on a data refresh. Map.forEachFeatureAtPixel hit-tests
  // against a frameState snapshot that's only refreshed on the next render
  // tick, not synchronously when a layer is removed; disposing a WebGL layer
  // (as Heatmap is) right after removeLayer() nulls out the very layerState
  // object that snapshot still points to, so an in-flight pointermove/click
  // crashes on "Cannot read properties of null (reading 'hasRenderer')".
  // Recreating the layer per update hit that race constantly; updating one
  // long-lived layer's source avoids it entirely.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const features = (heatmapPoints ?? []).map((point) => {
      const feature = new Feature({
        geometry: new Point(fromLonLat([point.lng, point.lat])),
      });
      // Heatmap's default weight function reads this property.
      feature.set('weight', point.weight);
      return feature;
    });

    if (!heatmapLayerRef.current) {
      // Nothing to show yet and no layer exists - skip creating one (and
      // its WebGL context) until there's actually data for it.
      if (features.length === 0) return;

      const heatmapLayer = new Heatmap({
        source: new VectorSource({ features }),
        radius: 20,
        blur: 15,
      });
      heatmapLayer.setZIndex(500);

      heatmapLayerRef.current = heatmapLayer;
      map.addLayer(heatmapLayer);
      return;
    }

    const source = heatmapLayerRef.current.getSource();
    source?.clear();
    source?.addFeatures(features);
  }, [heatmapPoints]);

  // Dispose the heatmap layer's WebGL resources only on unmount, when no
  // further pointer events can race the teardown (see note above).
  useEffect(() => {
    return () => {
      const map = mapRef.current;
      const layer = heatmapLayerRef.current;
      if (!layer) return;
      map?.removeLayer(layer);
      layer.dispose();
      heatmapLayerRef.current = null;
    };
  }, []);

  // One OL Overlay + portal container per visible cctv marker. When the
  // "Show CCTV" toggle is on (openCctvCardsOnClick false, e.g. PSC dashboard)
  // every camera's VideoMaps card is open at once while the marker exists.
  // When openCctvCardsOnClick is true only clicked markers' cards render (the
  // rest stay as empty, hidden overlay anchors).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const cctvMarkers = markers.filter((item) => item.cctv);
    const currentIds = new Set(cctvMarkers.map((item) => item.id));

    for (const [id, entry] of cctvOverlaysRef.current) {
      if (!currentIds.has(id)) {
        map.removeOverlay(entry.overlay);
        cctvOverlaysRef.current.delete(id);
      }
    }

    for (const item of cctvMarkers) {
      let entry = cctvOverlaysRef.current.get(item.id);
      if (!entry) {
        const container = document.createElement('div');
        const overlay = new Overlay({
          element: container,
          // Left-center of the card sits at the pin, so the card opens to
          // the right of the POI instead of covering it. Offset well past
          // the pin icon's own width so the card doesn't crowd it.
          positioning: 'center-left',
          offset: [36, 0],
          stopEvent: true,
          // Appended rather than inserted first (the OpenLayers default), so a
          // card created later also sits later in the DOM and is not painted
          // under the cards that already existed.
          insertFirst: false,
        });
        map.addOverlay(overlay);
        entry = { overlay, container };
        cctvOverlaysRef.current.set(item.id, entry);
      }
      entry.overlay.setPosition(fromLonLat([item.lng, item.lat]));
    }

    setCctvOverlayIds(cctvMarkers.map((item) => item.id));

    // If an open cctv marker disappeared (layer toggled off, camera dropped
    // off the list on refresh), drop it so it doesn't auto-reopen when an id
    // happens to come back.
    if (openCctvCardsOnClick) {
      setOpenCctvIds((prev) => {
        let changed = false;
        const next = new Set<string>();
        for (const id of prev) {
          if (currentIds.has(id)) {
            next.add(id);
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  }, [markers, openCctvCardsOnClick]);

  // Raise the card of the camera clicked last above every other open card, and
  // drop the previous one back. Applied to the OpenLayers wrapper element, not
  // to the card (see getOverlayWrapper). Iterating the rendered ids keeps
  // overlays created after a raise in step with the current top card.
  useEffect(() => {
    for (const id of cctvOverlayIds) {
      const entry = cctvOverlaysRef.current.get(id);
      const wrapper = entry ? getOverlayWrapper(entry.overlay) : null;
      if (wrapper) {
        wrapper.style.zIndex = id === topCctvId ? CCTV_RAISED_Z_INDEX : '';
      }
    }
  }, [topCctvId, cctvOverlayIds]);

  // useEffect(() => {
  //   if (!mapRef.current) return;

  //   if (polylineLayerRef.current) {
  //     mapRef.current.removeLayer(polylineLayerRef.current);
  //   }

  //   const lineFeatures =
  //     polylines?.map((line) => {
  //       const feature = new Feature({
  //         geometry: new LineString(line.points.map((point) => fromLonLat(point))),
  //       });

  //       feature.set('color', line.color);
  //       feature.set('width', line.width);

  //       return feature;
  //     }) || [];

  //   const layer = new VectorLayer({
  //     source: new VectorSource({
  //       features: lineFeatures,
  //     }),

  //     style: (feature) =>
  //       new Style({
  //         stroke: new Stroke({
  //           color: feature.get('color') || '#2196F3',
  //           width: feature.get('width') || 4,
  //         }),
  //       }),
  //   });

  //   polylineLayerRef.current = layer;

  //   mapRef.current.addLayer(layer);
  // }, [polylines]);

  // Ambulance/report card click (PSCDashboard) → fly to that marker's location
  // at the deepest zoom stage. Same pattern as MainDashboardMaps.tsx's own
  // focusTarget effect. Once the fly-to lands, the target marker (if `id` was
  // given) lights up with a glow a few times so it's easy to spot against the
  // rest of the map.
  useEffect(() => {
    if (!mapRef.current || !focusTarget) return;
    const map = mapRef.current;

    if (focusTarget.ids && focusTarget.ids.length > 1) {
      const targetFeatures = focusTarget.ids
        .map((id) => findFeature(id))
        .filter((f): f is Feature => !!f);

      const extent = createEmpty();
      targetFeatures.forEach((feature) => {
        const geometry = feature.getGeometry();
        if (geometry) extend(extent, geometry.getExtent());
      });
      if (isEmpty(extent)) return;

      // biome-ignore lint/suspicious/noFocusedTests: OpenLayers View.fit(), not a focused test (jasmine's fit()).
      map.getView().fit(extent, {
        padding: FIT_PADDING,
        maxZoom: MAX_FIT_ZOOM,
        duration: 500,
        callback: (completed) => {
          if (completed) blinkFeatures(targetFeatures);
        },
      });
      return;
    }

    if (focusTarget.lat == null || focusTarget.lng == null) return;

    map.getView().animate(
      {
        center: fromLonLat([focusTarget.lng, focusTarget.lat]),
        zoom: MAX_FIT_ZOOM,
        duration: 500,
      },
      (completed) => {
        if (!completed || !focusTarget.id) return;
        const feature = findFeature(focusTarget.id);
        if (feature) blinkFeatures([feature]);
      }
    );
  }, [focusTarget, findFeature, blinkFeatures]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (polylineLayerRef.current) {
      map.removeLayer(polylineLayerRef.current);
    }

    const features = (polylines || [])
      .filter((line) => line.points.length >= 2)
      .map((line) => {
        const feature = new Feature({
          geometry: new LineString(line.points.map((point) => fromLonLat(point))),
        });
        feature.set('color', line.color || '#00A2FF');
        feature.set('width', line.width || 4);
        return feature;
      });

    const routeLayer = new VectorLayer({
      source: new VectorSource({ features }),
      style: (feature) =>
        new Style({
          stroke: new Stroke({
            color: feature.get('color') || '#00A2FF',
            width: feature.get('width') || 4,
          }),
        }),
    });

    polylineLayerRef.current = routeLayer;
    map.addLayer(routeLayer);
  }, [polylines]);

  // useEffect(() => {
  //   if (!mapRef.current) return;

  //   if (polylineLayerRef.current) {
  //     mapRef.current.removeLayer(polylineLayerRef.current);
  //   }

  //   const lineFeatures = polylines?.map((line) => {
  //     return new Feature({
  //       geometry: new LineString(
  //         line.points.map((point) => fromLonLat(point))
  //       ),
  //     });
  //   });

  //   const lineLayer = new VectorLayer({
  //     source: new VectorSource({
  //       features: lineFeatures,
  //     }),
  //     style: (feature) => {
  //       const index = lineFeatures.indexOf(feature as any);
  //       const line = polylines?.[index];

  //       return new Style({
  //         stroke: new Stroke({
  //           color: line?.color || '#2196F3',
  //           width: line?.width || 4,
  //         }),
  //       });
  //     },
  //   });

  //   polylineLayerRef.current = lineLayer;

  //   mapRef.current.addLayer(lineLayer);
  // }, [polylines]);

  return (
    <div className="relative h-full w-full">
      <div ref={mapElementRef} className="h-full w-full SmartCityMaps" />

      <div
        ref={tooltipElementRef}
        style={{
          position: 'absolute',
          background: isDark ? '#1F2937' : '#FFFFFF',
          color: isDark ? '#FFFFFF' : '#111827',

          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '12px',
          display: 'none',
          pointerEvents: 'none',
          zIndex: 999,
          margin: '15px',
          minWidth: '180px',
        }}
      />
      {/* Mounted unconditionally (not gated on showModal) - a stacked-marker
          cluster click (see the singleclick handler's aggregate-list branch)
          shows this same list popup on every page, including ones that never
          set a per-marker modalTooltip (e.g. PSCDashboard). showModal only
          controls the plain hover-tooltip below, a separate element/effect. */}
      <div
        ref={modalTooltipElementRef}
        onWheelCapture={(event) => event.stopPropagation()}
        onTouchMoveCapture={(event) => event.stopPropagation()}
        onScrollCapture={(event) => event.stopPropagation()}
        style={{
          display: selectedModalTooltip ? 'block' : 'none',
          pointerEvents: 'auto',
        }}
      >
        <ModalMapTooltip {...selectedModalTooltip} />
      </div>

      {cctvOverlayIds.map((id) => {
        const entry = cctvOverlaysRef.current.get(id);
        const marker = markers.find((item) => item.id === id);
        if (!entry || !marker?.cctv) return null;
        const cctv = marker.cctv;
        // openCctvCardsOnClick: nothing shows until that marker is clicked;
        // every clicked camera's card stays open until the empty map is
        // clicked. All-open mode: every camera's card shows from the start.
        // Either way the camera clicked last is the card raised on top, via a
        // z-index on that overlay's wrapper element (see the raise effect).
        const isOpen = openCctvCardsOnClick ? openCctvIds.has(id) : true;
        if (!isOpen) return null;
        return createPortal(
          // biome-ignore lint/a11y/useSemanticElements: wraps VideoMaps' own <button> and native <video controls> - a native <button> can't contain either
          <div
            key={id}
            onClick={() => {
              // Clicking a card brings it to the front of the open ones.
              setTopCctvId(id);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setTopCctvId(id);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <VideoMaps
              name={cctv.name}
              streamUrl={cctv.streamUrl ?? ''}
              typeVideo={cctv.typeVideo}
              onClick={() => onCctvExpand?.(cctv)}
            />
          </div>,
          entry.container
        );
      })}

      {!isMapAvailable() && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-[14px] text-[#9AA8C2]">
          지도를 불러올 수 없습니다
        </div>
      )}

      {legend && legend.length > 0 && (
        <div className="absolute right-3 top-3 flex flex-col gap-2 pointer-events-none">
          {legend.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-2 bg-white rounded-full pl-2 pr-4 py-1.5 shadow-md"
            >
              <img src={item.icon} alt="" className="w-5 h-5 shrink-0" />
              <span className="text-[13px] font-medium text-[#1F2937] whitespace-nowrap">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Gate the map on the boot probe: mount the inner map only once the provider is
// resolved so its init effect (and the marker/focus effects that follow) run in
// the right order. Already-resolved is the common case (no loading flash).
const SmartCityMaps = (props: Props) => {
  const ready = useMapProviderReady();
  if (!ready) {
    return (
      <div
        className="w-full h-full flex items-center justify-center text-[14px] text-[#9AA8C2]"
        style={MAP_GRID_BG}
      >
        지도 불러오는 중...
      </div>
    );
  }
  return <SmartCityMapsInner {...props} />;
};

export default SmartCityMaps;
